
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Wallet, Users, DollarSign } from "lucide-react";
import { DashboardData } from "@/lib/types";

interface DashboardCardsProps {
  data: DashboardData;
  totalUniqueCustomers: number;
  totalValue: number;
  totalChitPoolValue: number;
  totalInterestCollected: number;
}

export const DashboardCards = ({
  data,
  totalUniqueCustomers,
  totalValue,
  totalInterestCollected
}: DashboardCardsProps) => {
  const navigate = useNavigate();
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
      <Card 
        className="bg-gradient-to-tr from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 border-blue-200 dark:border-blue-800 cursor-pointer hover:shadow-md transition-all"
        onClick={() => navigate("/chit-pools")}
      >
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl">Chit Pools</CardTitle>
            <BarChart className="h-5 w-5 text-blue-500" />
          </div>
          <CardDescription>Total active chit pools</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">{data?.chitPoolsCount || 0}</div>
        </CardContent>
      </Card>

      <Card 
        className="bg-gradient-to-tr from-green-50 to-green-100 dark:from-green-950 dark:to-green-900 border-green-200 dark:border-green-800 cursor-pointer hover:shadow-md transition-all"
        onClick={() => navigate("/loans")}
      >
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl">Loans</CardTitle>
            <Wallet className="h-5 w-5 text-green-500" />
          </div>
          <CardDescription>Total active loans</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">{data?.loansCount || 0}</div>
        </CardContent>
      </Card>

      <Card 
        className="bg-gradient-to-tr from-amber-50 to-amber-100 dark:from-amber-950 dark:to-amber-900 border-amber-200 dark:border-amber-800 cursor-pointer hover:shadow-md transition-all"
        onClick={() => navigate("/customers")}
      >
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl">Members</CardTitle>
            <Users className="h-5 w-5 text-amber-500" />
          </div>
          <CardDescription>Total unique customers</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">{totalUniqueCustomers}</div>
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-tr from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 border-purple-200 dark:border-purple-800">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl">Total Value</CardTitle>
            <DollarSign className="h-5 w-5 text-purple-500" />
          </div>
          <CardDescription>Combined portfolio value</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">
            {formatCurrency(totalValue)}
          </div>
          <div className="text-sm text-muted-foreground mt-1">
            Interest: {formatCurrency(totalInterestCollected || 0)}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
